import {useState} from 'react'
import {Button, Spinner} from 'react-bootstrap'
import {Document, Page, pdfjs} from 'react-pdf'
import {PdfFieldDefinition, PdfSignerRole} from '../../../types/pdfEnvelopesApi'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const FIELD_COLORS = ['#2980b9', '#27ae60', '#e74c3c', '#f39c12', '#8e44ad', '#16a085']
const FIELD_ICONS: Record<string, string> = {
  signature: '✍',
  initials: 'AB',
  full_name: '👤',
  date_signed: '📅',
  text: 'T',
  checkbox: '☑',
}

interface Props {
  pdfUrl: string
  fields: PdfFieldDefinition[]
  signerRoles: PdfSignerRole[]
  width?: number
}

export const PdfWithFieldOverlay = ({pdfUrl, fields, signerRoles, width = 700}: Props) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState<{w: number; h: number} | null>(null)

  const roleColor = (role: string) => {
    const idx = signerRoles.findIndex((r) => r.role === role)
    return FIELD_COLORS[idx >= 0 ? idx % FIELD_COLORS.length : 0]
  }

  const roleLabel = (role: string) =>
    signerRoles.find((r) => r.role === role)?.label ?? role

  const currentPageFields = fields.filter((f) => f.page === currentPage)

  const goTo = (page: number) => {
    setCurrentPage(page)
    setPageSize(null)
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <div
        className='d-flex align-items-center justify-content-center gap-3 py-2'
        style={{background: '#e9ecef', borderBottom: '1px solid #ddd', flexShrink: 0}}
      >
        <Button size='sm' variant='secondary' disabled={currentPage <= 1} onClick={() => goTo(currentPage - 1)}>
          &#8592;
        </Button>
        <span style={{fontSize: 13}}>Página {currentPage} / {totalPages}</span>
        <Button size='sm' variant='secondary' disabled={currentPage >= totalPages} onClick={() => goTo(currentPage + 1)}>
          &#8594;
        </Button>
      </div>

      <div style={{flex: 1, overflowY: 'auto', background: '#e9ecef', display: 'flex', justifyContent: 'center', padding: 16}}>
        <div style={{position: 'relative', display: 'inline-block'}}>
          <Document
            file={pdfUrl}
            onLoadSuccess={({numPages}) => setTotalPages(numPages)}
            loading={
              <div className='d-flex justify-content-center align-items-center' style={{height: 400}}>
                <Spinner animation='border' />
              </div>
            }
          >
            <Page
              pageNumber={currentPage}
              width={width}
              onRenderSuccess={(page) => setPageSize({w: page.width, h: page.height})}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </Document>

          {pageSize &&
            currentPageFields.map((field) => {
              const px = field.x * pageSize.w
              const py = field.y * pageSize.h
              const fw = field.width * pageSize.w
              const fh = field.height * pageSize.h
              const color = roleColor(field.signerRole)

              return (
                <div
                  key={field.id}
                  title={`${field.label} — ${roleLabel(field.signerRole)}`}
                  style={{
                    position: 'absolute',
                    left: px,
                    top: py,
                    width: fw,
                    height: fh,
                    border: `2px dashed ${color}`,
                    background: `${color}28`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: Math.max(8, fh * 0.38),
                    color,
                    fontWeight: 'bold',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {FIELD_ICONS[field.type] ?? ''}{fh > 14 ? ` ${field.label}` : ''}
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
